import { isDefined, isNonEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { LightAlert } from 'components/shared/LightAlert/LightAlert';
import { GreeksTables } from 'components/shared/Sensitivity/Greeks/FICCGreeks';
import { Greeks } from 'components/shared/Sensitivity/Greeks/Greeks';
import { SensitivitySection } from 'components/shared/Sensitivity/SensitivitySection/SensitivitySection';
import { SpTooltip } from 'components/shared/Tooltip/Tooltip';
import type { ApiErrorMessage } from 'epic/apiErrorEpicHelper';
import { IntlKey } from 'locales';
import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { AssetClass } from 'state/blotters/priceImprovement/priceImprovementBlotter.model';
import type { Fee, FICCGreek, GreeksTable, Metric } from 'state/quotes/details/details.model';
import {
  getUserPreferencesFeesPrecision,
  getUserPreferencesGreeksPrecision,
  getUserPreferencesMetricsPrecision,
} from 'state/selectors';

import { useFetchSensitivity } from '../PriceImprovement/Grid/masterDetail/useFetchSensitivity';
import { SensitivitySkeleton } from './sensitivitySkeleton';

interface SensitivityProps {
  className?: string;
  assetClass?: AssetClass;
  greeks?: GreeksTable;
  ficcGreeks?: readonly FICCGreek[];
  greeksPrecision?: number;
  feesPrecision?: number;
  metricsPrecision?: number;
  fees?: readonly Fee[];
  metrics?: readonly Metric[];

  feesDetailLink?: string;

  paddingLeft: number;
}

export const Sensitivity: FC<SensitivityProps> = ({
  assetClass,
  greeks,
  ficcGreeks,
  greeksPrecision,
  fees,
  feesPrecision,
  metrics,
  metricsPrecision,
  feesDetailLink,
  className,
  paddingLeft,
}) => {
  const { formatMessage } = useIntl();

  const hasFiccGreeks = isNonEmpty(ficcGreeks);
  const hasGreeks = isDefined(greeks) && isNonEmpty(greeks.cells);
  const hasFees = isNonEmpty(fees);
  const hasMetrics = isNonEmpty(metrics);

  const hasSensitivity = hasGreeks || hasFees || hasMetrics;
  return hasSensitivity ? (
    <div
      style={{ paddingLeft }}
      className={classNames('bg-lvl1 d-flex flex-row justify-content-end flex-wrap', className)}
    >
      {!hasFiccGreeks && hasGreeks && (
        <Greeks
          assetClass={assetClass}
          data={greeks}
          className="col py-2"
          noGreeksMessageKey="quoteDetails.NoGreeksAvailable"
          titleKey="quoteDetails.Tab.Greeks"
          copyActionName="Copy greek"
          precision={greeksPrecision ?? 4}
        />
      )}
      {hasFiccGreeks && <GreeksTables greeks={ficcGreeks} greeksPrecision={greeksPrecision ?? 4} />}
      {hasFees && (
        <SensitivitySection
          sectionName="Fees"
          unavailableDataMessageKey="quoteDetails.NoFeesAvailable"
          copyActionName="Copy Fees"
          data={fees}
          precision={feesPrecision ?? 4}
          className="col-6 py-2"
          headerKey="quoteDetails.Tab.Fees"
        >
          {feesDetailLink && (
            <a className="btn btn-flat-secondary btn-icon-end py-2" href={feesDetailLink!} target="_blank">
              {formatMessage({ id: 'details' })}
              <i className="icon ps-1">download</i>
            </a>
          )}
        </SensitivitySection>
      )}
      {hasMetrics && (
        <SensitivitySection
          headerKey="quoteDetails.Tab.Metrics"
          sectionName="Metrics"
          unavailableDataMessageKey="quoteDetails.NoMetricsAvailable"
          data={metrics}
          className="col-6 py-2"
          precision={metricsPrecision ?? 4}
          copyActionName="Copy metrics"
        />
      )}
    </div>
  ) : (
    <LightAlert severity="secondary" e2e="sensitivity-unavailable-alert">
      {formatMessage({ id: 'priceImprovementBlotter.sensitivity.notAvailable' })}
    </LightAlert>
  );
};

export interface SensitivityContainerProps {
  referenceQuoteId: string;
  className?: string;
  paddingLeft: number;
  shouldRefresh?: boolean;
}

export const SensitivityContainer: FC<SensitivityContainerProps> = ({
  referenceQuoteId,
  className,
  paddingLeft,
  shouldRefresh = false,
}) => {
  const [fetchStatus, sensitivity, error, onRetry] = useFetchSensitivity(referenceQuoteId, shouldRefresh);
  const greeksPrecision = useSelector(getUserPreferencesGreeksPrecision);
  const feesPrecision = useSelector(getUserPreferencesFeesPrecision);
  const metricsPrecision = useSelector(getUserPreferencesMetricsPrecision);
  const fees = useMemo(() => sensitivity?.fees?.filter(fee => !fee.name.includes('http')), [sensitivity]);
  const feesDetailLink = useMemo(() => sensitivity?.fees?.find(fee => fee.name.includes('http')), [sensitivity]);

  return (
    <div>
      {fetchStatus === 'success' && (
        <Sensitivity
          paddingLeft={paddingLeft}
          className={className}
          assetClass={sensitivity?.assetClass}
          greeks={sensitivity?.greeks}
          ficcGreeks={sensitivity?.FICCGreeks}
          greeksPrecision={greeksPrecision}
          fees={fees}
          feesDetailLink={feesDetailLink?.name}
          feesPrecision={feesPrecision}
          metrics={sensitivity?.metrics}
          metricsPrecision={metricsPrecision}
        />
      )}
      {fetchStatus === 'loading' && <SensitivitySkeleton className="w-100" />}
      {(fetchStatus === 'error' || fetchStatus === 'unauthorized') && (
        <Error referenceQuoteId={referenceQuoteId} onRetry={onRetry} error={error} />
      )}
    </div>
  );
};

const Error: FC<{
  className?: string;
  error?: ApiErrorMessage;
  referenceQuoteId: string;
  onRetry?: () => void;
}> = ({ className, referenceQuoteId, error, onRetry }) => {
  const { formatMessage } = useIntl();
  const errorMessage = error ? formatMessage({ id: error.message as IntlKey }) : null;
  const messageId = `sensitivity-error-message-${referenceQuoteId}`;

  return (
    <LightAlert severity="secondary" e2e="sensitivity-error" className={classNames('w-100 py-4 px-5', className)}>
      <SpTooltip disabled={!errorMessage} color="warning" contents={errorMessage}>
        <span id={messageId} data-e2e="sensitivity-error-message" className={classNames({ clickable: !!error })}>
          {formatMessage({ id: 'discover.fetchData.error' })}
        </span>
      </SpTooltip>
      <em className="flex-grow-1" />
      {onRetry && (
        <button
          className="btn btn-sm btn-outline-secondary float-end"
          onClick={onRetry}
          data-e2e="sensitivity-error-retry-button"
        >
          {formatMessage({
            id: 'discover.fetchData.retry',
            defaultMessage: 'retry',
          })}
        </button>
      )}
    </LightAlert>
  );
};
